import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MODULES } from '../../modules';
import { getModulePolicy, saveModulePolicy, resetModulePolicy, ModuleLevelChoice, ModulePolicyOverview } from '../../utils/api/modpolicy';

// Admin → Roles → Module access: which modules only admins, or only the Owner role, may open.
// The server (services/modulePolicy.ts) is the single point; this card edits its workspace setting.

const input: React.CSSProperties = { padding: '5px 8px', fontSize: 12.5, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none' };
const btn = (bg: string, color = '#fff'): React.CSSProperties => ({ padding: '7px 12px', borderRadius: 'var(--radius-md)', border: 'none', background: bg, color, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' });
const card: React.CSSProperties = { background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 16, marginBottom: 12 };

/** The modules kept to admins by the live policy — the Admin user grid hides their checkboxes (a grant
 *  would be inert). Until the policy has loaded it falls back to the catalogue's adminOnly marks. */
export function useAdminOnlyModules(): Set<string> {
  const [keys, setKeys] = useState<Set<string> | null>(null);
  useEffect(() => {
    let alive = true;
    getModulePolicy()
      .then(d => { if (alive) setKeys(new Set(Object.keys(d.policy).filter(k => d.policy[k] === 'admin'))); })
      .catch(() => { /* keep the fallback */ });
    return () => { alive = false; };
  }, []);
  return useMemo(() => keys ?? new Set(MODULES.filter(m => m.adminOnly).map(m => m.key)), [keys]);
}

export default function ModuleAccess() {
  const { t } = useTranslation();
  const [data, setData] = useState<ModulePolicyOverview | null>(null);
  const [draft, setDraft] = useState<Record<string, ModuleLevelChoice>>({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(''); const [msg, setMsg] = useState('');

  const adopt = (d: ModulePolicyOverview) => {
    setData(d);
    setDraft(Object.fromEntries(d.modules.map(m => [m.key, d.policy[m.key] || 'everyone'])));
  };
  useEffect(() => { getModulePolicy().then(adopt).catch((e: any) => setErr(e.message)); }, []);

  if (!data) return err ? <div style={{ ...card, color: '#E24B4A', fontSize: 13 }}>{err}</div> : null;

  const levelOf = (k: string): ModuleLevelChoice => data.policy[k] || 'everyone';
  const dirty = data.modules.some(m => draft[m.key] !== levelOf(m.key));
  const anyOwner = Object.values(draft).includes('owner');
  const flash = (s: string) => { setMsg(s); setTimeout(() => setMsg(''), 2000); };

  const save = async () => {
    setSaving(true); setErr(''); setMsg('');
    try { adopt(await saveModulePolicy(draft)); flash(t('cs_saved')); }
    catch (e: any) { setErr(e.message); } finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm(t('modacc_reset_confirm'))) return;
    setSaving(true); setErr(''); setMsg('');
    try { adopt(await resetModulePolicy()); flash(t('cs_saved')); }
    catch (e: any) { setErr(e.message); } finally { setSaving(false); }
  };

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{t('modacc_title')}</div>
        <code style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>module_policy</code>
        <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{data.custom ? t('modacc_using_custom') : t('modacc_using_default')}</span>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', margin: '2px 0 10px', lineHeight: 1.55 }}>{t('modacc_intro')}</div>
      {anyOwner && data.ownerCount === 0 && (
        <div style={{ fontSize: 12.5, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 11px', marginBottom: 10 }}>{t('modacc_no_owner')}</div>
      )}
      {!data.mayChangeOwner && (
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 10 }}><i className="ti ti-lock" /> {t('modacc_owner_locked')}</div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(380px, 100%), 1fr))', gap: '4px 16px', marginBottom: 12 }}>
        {data.modules.map(m => {
          const v = draft[m.key] || 'everyone';
          const lockedRow = !data.mayChangeOwner && levelOf(m.key) === 'owner';
          const changed = (data.defaultPolicy[m.key] || 'everyone') !== v;
          return (
            <div key={m.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, padding: '4px 6px', borderRadius: 6, background: v !== 'everyone' ? 'var(--bg-active)' : 'transparent' }}>
              <i className={`ti ti-${m.icon}`} style={{ fontSize: 14, color: 'var(--accent)' }} aria-hidden />
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-primary)' }} title={m.key}>
                {t(`mod_${m.key}`)}
                {m.shared && <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}> · {t('modacc_shared')}</span>}
                {changed && <span style={{ fontSize: 11, color: '#B45309' }} title={t('modacc_changed')}> ●</span>}
              </span>
              <select value={v} disabled={lockedRow || saving} onChange={e => setDraft(d => ({ ...d, [m.key]: e.target.value as ModuleLevelChoice }))} style={input}>
                <option value="everyone">{m.shared ? t('modacc_everyone_shared') : t('modacc_everyone')}</option>
                <option value="admin">{t('modacc_admin')}</option>
                <option value="owner" disabled={!data.mayChangeOwner}>{t('modacc_owner')}</option>
              </select>
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {data.custom && <button onClick={reset} disabled={saving} style={btn('var(--c-gray-100)', 'var(--text-secondary)')}>{t('modacc_reset')}</button>}
        <div style={{ flex: 1 }} />
        {dirty && !msg && <span style={{ fontSize: 12, color: '#B45309' }}>{t('modacc_unsaved')}</span>}
        {err && <span style={{ fontSize: 12, color: '#E24B4A' }}>{err}</span>}
        {msg && <span style={{ fontSize: 12, color: '#1D9E75' }}><i className="ti ti-check" /> {msg}</span>}
        <button onClick={save} disabled={saving || !dirty} style={btn('var(--accent)')}>{saving ? t('login_wait') : t('save')}</button>
      </div>
    </div>
  );
}
